import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, RefreshCw, Loader2, Search, ScrollText, FileDown, Share2 } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";

const RTYPES = [
  { v: "", label: "Tüm kayıt türleri" },
  { v: "van_sale", label: "Rota Fişi (görüntüleme)" },
  { v: "van_sale_pdf", label: "Rota Fişi (PDF)" },
  { v: "van_sale_share", label: "Rota Fişi (paylaşım)" },
  { v: "van_performance", label: "Performans Panosu" },
  { v: "van_geo_map", label: "Konum Haritası" },
  { v: "field_visits_export", label: "Saha Ziyaret Dökümü" },
];
const ACTION_LABEL = { view: "Görüntüledi", export: "Dışa aktardı", share: "Paylaştı" };
const ACTION_STYLE = { view: "bg-slate-100 text-slate-600", export: "bg-amber-100 text-amber-700", share: "bg-sky-100 text-sky-700" };
const ACTION_ICON = { export: FileDown, share: Share2 };
const dt = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return iso; } };

export default function AccessAudit() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rtype, setRtype] = useState("");
  const [date, setDate] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (rtype) params.resource_type = rtype;
      if (date) params.date = date;
      const [l, s] = await Promise.all([
        api.get("/access-logs", { params }),
        api.get("/access-logs/summary", { params: { days: 30 } }),
      ]);
      setRows(l.data || []);
      setSummary(s.data || null);
    } catch (e) {
      toast.error("Erişim izi yüklenemedi (yalnız Yönetici görüntüler).");
    } finally { setLoading(false); }
  }, [rtype, date]);
  useEffect(() => { load(); }, [load]);

  const ql = q.trim().toLocaleLowerCase("tr-TR");
  const filtered = ql ? rows.filter((r) => [r.user_email, r.user_name, r.resource_label, r.resource_type_label, r.role]
    .some((x) => String(x || "").toLocaleLowerCase("tr-TR").includes(ql))) : rows;
  const paged = usePaged(filtered, 20);

  return (
    <div className="space-y-4" data-testid="access-audit-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900"><Eye className="h-5 w-5 text-emerald-400" /></div>
          <div>
            <p className="overline text-slate-400">Analiz & Onay · Denetim</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Erişim İzi (Kim, Neyi, Ne Zaman Gördü)</h1>
          </div>
        </div>
        <Button data-testid="aa-refresh" variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="aa-summary">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium text-slate-400">Son 30 gün toplam erişim</p>
          <p className="mt-1 font-heading text-3xl font-bold text-slate-900" data-testid="aa-total">{summary?.total ?? "—"}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-400"><ScrollText className="h-3.5 w-3.5" /> En çok görüntülenen kayıt türleri</p>
          <div className="space-y-1">
            {(summary?.by_type || []).slice(0, 3).map((t) => (
              <div key={t.type} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{t.label}</span>
                <span className="font-semibold text-slate-800">{t.count}</span>
              </div>
            ))}
            {(!summary || (summary.by_type || []).length === 0) && <p className="text-sm text-slate-400">Kayıt yok</p>}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="mb-2 text-xs font-medium text-slate-400">En aktif kullanıcılar</p>
          <div className="space-y-1">
            {(summary?.top_users || []).slice(0, 3).map((u) => (
              <div key={u.user} className="flex items-center justify-between text-sm">
                <span className="truncate text-slate-600">{u.user}</span>
                <span className="ml-2 font-semibold text-slate-800">{u.count}</span>
              </div>
            ))}
            {(!summary || (summary.top_users || []).length === 0) && <p className="text-sm text-slate-400">Kayıt yok</p>}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="aa-filter-type" value={rtype} onChange={(e) => setRtype(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
          {RTYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        <Input data-testid="aa-filter-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" />
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="aa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kişi, kayıt veya tür ara…" className="h-9 pl-8" />
        </div>
        {(rtype || date) && <Button data-testid="aa-clear" variant="ghost" size="sm" onClick={() => { setRtype(""); setDate(""); }}>Filtreyi temizle</Button>}
      </div>

      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-3 py-3">Tarih-Saat</th><th className="px-3 py-3">Kullanıcı</th><th className="px-3 py-3">Rol</th>
              <th className="px-3 py-3">Kayıt Türü</th><th className="px-3 py-3">Kayıt / Hedef</th><th className="px-3 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Yükleniyor…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} data-testid="aa-empty" className="px-4 py-10 text-center text-slate-400">Kayıt yok. Hassas kayıtlar (fiş, performans, harita) görüntülendikçe burada listelenir.</td></tr>}
            {paged.slice.map((r) => {
              const AI = ACTION_ICON[r.action];
              return (
                <tr key={r.id} data-testid={`aa-row-${r.id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{dt(r.at)}</td>
                  <td className="px-3 py-2.5"><span className="text-slate-700">{r.user_name || r.user_email}</span></td>
                  <td className="px-3 py-2.5">{r.role && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{r.role}</span>}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.resource_type_label}</td>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r.resource_label || "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ACTION_STYLE[r.action] || ACTION_STYLE.view}`}>
                      {AI && <AI className="h-3 w-3" />} {ACTION_LABEL[r.action] || r.action}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total} onPage={paged.setPage} onPageSize={paged.setPageSize} testid="access-audit-pagination" />
    </div>
  );
}
